import { useCallback, useEffect, useRef, useState } from 'react'

import { formatShortDate } from '@app/shared'
import { callNativePlugin, nativeRecorderAvailable, type NativeResult } from './native-recorder'
import { clockFromTimestamp, localDayOf } from './time'

// ============================================================================
// THE PHONE RINGS ITS OWN ALARMS — the web half of the Android app's alert scheduler.
//
// WHY (owner, 2026-09-25). Web Push reaches Chrome. The installed LifeOS app is a WebView, and it
// got no reminder, calendar-block or Google-event notifications at all once the Chrome shortcut was
// gone. So the phone now pulls its own next 24 hours from the Worker
// (`GET /api/notifications/upcoming`) every 15 minutes and arms one exact alarm per item — the
// mechanism the class prompt proved on his S24 (docs/NATIVE-SPIKE.md). It rides on the device
// capture token the phone uploader already holds: turning that on (Recordings) is what enables it.
//
// WHAT THIS FILE IS. The two plugin commands, feature-detected exactly like native-recorder.ts —
// nothing here throws, and a browser tab, an older app build and a working phone are three
// different answers, never one quiet "nothing":
//
//   notifystate → { enabled, lastPullAt?, lastPullError?, armedCount, nextFireAt?, firedLast24h,
//                   exactAlarms?, notificationsAllowed? }       (never the token)
//   notifypull  → the same, after checking with LifeOS now
// ============================================================================

/** What the phone says about its own alert alarms. Optional fields are genuinely absent. */
export interface NativeNotifyState {
  /** The phone holds a device token, so it can ask LifeOS for alerts. */
  enabled: boolean
  /** The last check with LifeOS, successful or not. Absent: it has never checked. */
  lastPullAt?: string | null
  /** Why the last check did not work, in the phone's (or the Worker's) words. */
  lastPullError?: string | null
  /** Alarms set right now. */
  armedCount: number
  /** The soonest one. Absent when none is set. */
  nextFireAt?: string | null
  /** Alerts this phone rang in the last 24 hours. */
  firedLast24h: number
  /** False: Android refused exact alarms, so these may ring late. Absent on an older build. */
  exactAlarms?: boolean
  /** False: notifications (or the "Reminders and calendar" channel) are switched off. */
  notificationsAllowed?: boolean
}

function normalize(answer: NativeResult<NativeNotifyState>): NativeResult<NativeNotifyState> {
  if (!answer.available) return answer
  const v = answer.value
  if (
    typeof v?.enabled !== 'boolean' ||
    typeof v.armedCount !== 'number' ||
    typeof v.firedLast24h !== 'number'
  ) {
    return { available: false, reason: 'The app answered with something that is not its alerts state.' }
  }
  return answer
}

/** What the phone's alert alarms are doing. An app build older than them answers `available: false`. */
export async function nativeNotifyState(): Promise<NativeResult<NativeNotifyState>> {
  return normalize(await callNativePlugin<NativeNotifyState>('plugin:lifeos-recorder|notifystate'))
}

/** Check with LifeOS now, then answer as `nativeNotifyState`. A failed check is in `lastPullError`. */
export async function nativeNotifyPull(): Promise<NativeResult<NativeNotifyState>> {
  return normalize(await callNativePlugin<NativeNotifyState>('plugin:lifeos-recorder|notifypull'))
}

/** The screen's view of it. */
export type PhoneAlerts =
  /** A browser tab: the card does not exist, and Web Push is the whole story. */
  | { kind: 'browser' }
  /** Inside the app, not answered yet. */
  | { kind: 'asking' }
  /** Inside the app, but this build cannot ring its own alerts (the command is missing). */
  | { kind: 'unsupported'; reason: string }
  | { kind: 'ready'; state: NativeNotifyState }

/**
 * Asked once on mount and again whenever the screen comes back into view (the owner may have just
 * turned the phone uploader on, or come back from Android's notification settings), plus the one
 * action, "Check now".
 */
export function usePhoneAlerts(): {
  alerts: PhoneAlerts
  checking: boolean
  checkNow: () => void
} {
  const [alerts, setAlerts] = useState<PhoneAlerts>(() =>
    nativeRecorderAvailable() ? { kind: 'asking' } : { kind: 'browser' },
  )
  const [checking, setChecking] = useState(false)
  const live = useRef(true)

  const take = useCallback((answer: NativeResult<NativeNotifyState>) => {
    if (!live.current) return
    setAlerts(
      answer.available
        ? { kind: 'ready', state: answer.value }
        : { kind: 'unsupported', reason: answer.reason },
    )
  }, [])

  const ask = useCallback(async () => {
    if (!nativeRecorderAvailable()) return
    take(await nativeNotifyState())
  }, [take])

  useEffect(() => {
    live.current = true
    void ask()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void ask()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live.current = false
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [ask])

  const checkNow = useCallback(() => {
    setChecking(true)
    void nativeNotifyPull()
      .then(take)
      .finally(() => {
        if (live.current) setChecking(false)
      })
  }, [take])

  return { alerts, checking, checkNow }
}

/**
 * '4:05pm' today, 'Sep 26, 9am' on another day — read in the viewer's zone. Passed `nowMs` so the
 * words are testable.
 */
export function alertStamp(iso: string, nowMs: number, timeZone?: string): string {
  const clock = clockFromTimestamp(iso, timeZone)
  const day = localDayOf(iso, timeZone)
  const today = localDayOf(new Date(nowMs).toISOString(), timeZone)
  return day === today ? clock : `${formatShortDate(day)}, ${clock}`
}
